/**
 * Word'den yapıştırılan görselleri kurtarma.
 *
 * SORUN: Word'den metin + görsel kopyalandığında tarayıcı panoya iki ayrı
 * biçim koyar:
 *   - text/html : görseller `<img src="file:///C:/.../clip_image001.png">`
 *                 şeklinde YEREL dosya yolu ile gelir. Tarayıcı güvenlik
 *                 gereği bu yolu okuyamaz → görsel kaybolur.
 *   - text/rtf  : görsellerin GERÇEK baytları burada, hex kodlu olarak durur.
 *
 * CKEditor5'in ücretsiz PasteFromOffice eklentisi RTF'i çözmez (bu özellik
 * ticari "Paste from Office Enhanced" pakedinde). Bu yüzden görseller
 * src'siz kalıyor ve içerik `<img data-ck-upload-id="...">` gibi boş
 * etiketlerle kaydediliyordu.
 *
 * ÇÖZÜM: RTF'ten görsel baytlarını çıkar, sunucuya yükle, HTML'deki
 * erişilemeyen img'lerin src'sini gerçek adresle değiştir.
 */

/** RTF içindeki \pict gruplarından görselleri çıkarır */
export function extractRtfImages(rtf) {
  if (!rtf || typeof rtf !== 'string') return [];

  const images = [];
  // \pict grupları: \pict ... \<tip>blip ... <hex veri> }
  const pictRegex = /\{\\pict([\s\S]*?)\}/g;
  let match;

  while ((match = pictRegex.exec(rtf)) !== null) {
    const block = match[1];

    let mime = null;
    if (/\\pngblip/.test(block)) mime = 'image/png';
    else if (/\\jpegblip/.test(block)) mime = 'image/jpeg';
    else continue; // wmf/emf gibi vektör biçimleri atlanır

    // Kontrol sözcüklerinden sonraki hex veri
    const hexPart = block
      .replace(/\\[a-zA-Z]+-?\d*\s?/g, '') // kontrol sözcükleri
      .replace(/[^0-9a-fA-F]/g, '');       // sadece hex kalsın

    if (hexPart.length < 32) continue; // anlamsız derecede küçük

    const bytes = hexToBytes(hexPart);
    if (!bytes) continue;

    images.push({ mime, bytes });
  }

  return images;
}

function hexToBytes(hex) {
  const clean = hex.length % 2 === 0 ? hex : hex.slice(0, -1);
  const len = clean.length / 2;
  const out = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    const b = parseInt(clean.substr(i * 2, 2), 16);
    if (Number.isNaN(b)) return null;
    out[i] = b;
  }
  return out;
}

/**
 * SINIF İLE VERİLEN RENKLER
 *
 * Word, rengi her zaman `<span style="color:...">` şeklinde vermez; bir stile
 * (Başlık, tema rengi vb.) bağlıysa rengi belgenin `<style>` bloğunda SINIF
 * olarak tanımlar: `span.kirmizi {color:#C00000}`. `<style>` etiketi güvenlik
 * gereği korunmadığı için bu renkler yapıştırma anında kayboluyordu.
 *
 * Aşağıdaki iki yardımcı, o bloktaki sınıf kurallarını okuyup ilgili
 * elemanlara SATIR İÇİ yazar.
 *
 * ⚠️ Yalnızca `color` ve `background-color` alınır. `font-family`/`font-size`
 * de alınsaydı Word'ün varsayılan Calibri 11pt'si bütün metne yapışırdı.
 */
export function sinifRenkleri(html) {
  const harita = {};
  if (!html || typeof html !== 'string') return harita;

  const styleBloklari = html.match(/<style[^>]*>([\s\S]*?)<\/style>/gi) || [];
  styleBloklari.forEach((blok) => {
    const govde = blok.replace(/<\/?style[^>]*>/gi, '').replace(/<!--|-->/g, '');
    const kuralRegex = /([^{}]+)\{([^{}]*)\}/g;
    let kural;

    while ((kural = kuralRegex.exec(govde)) !== null) {
      const bildirimler = {};
      kural[2].split(';').forEach((bildirim) => {
        const ayrac = bildirim.indexOf(':');
        if (ayrac < 0) return;
        const ad = bildirim.slice(0, ayrac).trim().toLowerCase();
        const deger = bildirim.slice(ayrac + 1).trim();
        if ((ad === 'color' || ad === 'background-color') && deger) bildirimler[ad] = deger;
      });
      if (Object.keys(bildirimler).length === 0) continue;

      // "p.MsoNormal, span.kirmizi" gibi birden fazla seçici olabilir
      kural[1].split(',').forEach((secici) => {
        const eslesme = secici.trim().match(/\.([A-Za-z0-9_-]+)$/);
        if (eslesme) harita[eslesme[1]] = { ...harita[eslesme[1]], ...bildirimler };
      });
    }
  });

  return harita;
}

/** Sınıf renklerini elemanların style özniteliğine yazar; yazdıysa true döner */
export function satirIciRenkUygula(doc, harita) {
  const siniflar = Object.keys(harita);
  if (siniflar.length === 0) return false;

  let yazildi = false;
  siniflar.forEach((sinif) => {
    doc.querySelectorAll(`.${CSS.escape(sinif)}`).forEach((el) => {
      Object.entries(harita[sinif]).forEach(([ad, deger]) => {
        // Elemanın kendi satır içi rengi varsa ona dokunulmaz; o daha özeldir.
        if (!el.style.getPropertyValue(ad)) {
          el.style.setProperty(ad, deger);
          yazildi = true;
        }
      });
    });
  });

  return yazildi;
}

/** Tarayıcının erişemeyeceği (dolayısıyla kaybolacak) img'leri bulur */
export function findUnusableImages(doc) {
  return Array.from(doc.querySelectorAll('img')).filter((img) => {
    const src = img.getAttribute('src') || '';
    if (!src) return true;                       // src hiç yok
    if (src.startsWith('file://')) return true;  // yerel dosya — okunamaz
    if (/^[a-zA-Z]:\\/.test(src)) return true;   // C:\... yolu
    return false;
  });
}

/**
 * Yapıştırılan HTML'i onarır.
 * @param {string} html         panodaki text/html
 * @param {string} rtf          panodaki text/rtf
 * @param {(blob:Blob)=>Promise<string>} uploadFn  görseli yükleyip URL döner
 * @returns {Promise<{html:string, recovered:number, dropped:number}>}
 */
export async function repairWordHtml(html, rtf, uploadFn) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Sınıfla verilen renkler satır içine alınır (bkz. sinifRenkleri)
  const renkYazildi = satirIciRenkUygula(doc, sinifRenkleri(html));

  const broken = findUnusableImages(doc);
  if (broken.length === 0) {
    return { html: renkYazildi ? doc.body.innerHTML : html, recovered: 0, dropped: 0 };
  }

  const rtfImages = extractRtfImages(rtf);
  let recovered = 0;
  let dropped = 0;

  for (let i = 0; i < broken.length; i++) {
    const img = broken[i];
    const data = rtfImages[i];

    if (!data) {
      // Karşılığı yok — boş etiket bırakmak yerine tamamen kaldır.
      // (Kaydedilen içerikte src'siz img birikmesinin sebebi buydu.)
      img.remove();
      dropped++;
      continue;
    }

    try {
      const blob = new Blob([data.bytes], { type: data.mime });
      const url = await uploadFn(blob);
      img.setAttribute('src', url);
      img.removeAttribute('data-ck-upload-id');
      recovered++;
    } catch (e) {
      console.warn('Word görseli yüklenemedi:', e);
      img.remove();
      dropped++;
    }
  }

  return { html: doc.body.innerHTML, recovered, dropped };
}
